import { useState, useEffect, useCallback } from 'react';

interface StorageConfig<T> {
  key: string;
  initialValue?: T;
  queryValue?: string; // Assumed to be string if present
}

/**
 * Custom hook for persisting calculator values in localStorage
 * and initializing from either localStorage or URL query parameters
 */
export function useCalculatorStorage<T = string>({
  key,
  initialValue = '' as unknown as T,
  queryValue,
}: StorageConfig<T>): [T, (newValue: T) => void] {
  // Note: return type of setter specified

  const [internalValue, setInternalValue] = useState<T>(() => {
    // Initializer Priority: queryValue > localStorage > initialValue
    // console.log(`[${key}] useState initializer. queryValue: '${queryValue}'`);
    if (queryValue && queryValue.trim() !== '') {
      // console.log(`[${key}] useState using queryValue: '${queryValue}'`);
      return queryValue as unknown as T;
    }
    const savedValue = localStorage.getItem(key);
    // console.log(`[${key}] useState: read from localStorage for key '${key}'. Found: '${savedValue}'`);
    // If savedValue is a string (even an empty one), use it.
    // If savedValue is null (key not found by spec) or undefined (non-standard but possible in tests), fall back.
    if (typeof savedValue === 'string') {
      // console.log(`[${key}] useState using localStorage value: '${savedValue}'`);
      return savedValue as unknown as T;
    }
    // console.log(`[${key}] useState using initialValue: '${initialValue}'`);
    return initialValue;
  });
  // console.log(`[${key}] After useState. Current state ('internalValue'): '${internalValue}'`);

  // Custom setter that updates state and persists to localStorage.
  // This is what components will call (e.g., for onClick handlers).
  const setValue = useCallback(
    (newValue: T) => {
      setInternalValue(newValue);
      if (newValue !== undefined) {
        // console.log(`[${key}] setValue (user interaction) called with '${newValue}'. Saving to localStorage.`);
        localStorage.setItem(key, newValue as string);
      } else {
        // console.log(`[${key}] setValue (user interaction) called with undefined. Removing from localStorage.`);
        localStorage.removeItem(key); // Or set to initialValue string?
      }
    },
    [key]
  ); // setInternalValue and key are stable dependencies (setInternalValue from useState is stable)

  // Effect to handle changes to queryValue after initial load.
  // If queryValue changes and is different from current state, it should update the state and also persist to localStorage.
  useEffect(() => {
    // console.log(`[${key}] queryValue-Effect triggered. queryValue: '${queryValue}', Current state ('internalValue'): '${internalValue}'`);
    if (queryValue && queryValue.trim() !== '' && queryValue !== (internalValue as unknown as string)) {
      // console.log(`[${key}] queryValue ('${queryValue}') is new or different from state ('${internalValue}'). Updating state and localStorage.`);
      setInternalValue(queryValue as unknown as T);
      localStorage.setItem(key, queryValue as string);
    }
    // If queryValue matches internalValue, or queryValue is undefined/empty, this effect does nothing.
    // The state was already set correctly by useState or previous user interaction.
  }, [key, queryValue, internalValue]); // internalValue is needed to compare against queryValue

  return [internalValue, setValue];
}
